<template>
  <div class="container-fluid">
    <!-- Bread-crumb-->
    <div class="block-header">
      <bread-crumb></bread-crumb>
    </div>
    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <!-- Header -->
          <div class="header d-flex justify-content-between align-items-center">
            <h2>Quick Rate Update</h2>
            <router-link to="/fx-rates" class="btn btn-outline-secondary">
              <i class="fa fa-arrow-left mr-2"></i>
              Back to FX Rates
            </router-link>
          </div>
          <!-- Main Content -->
          <div class="body pt-0">
            <div class="p-4">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="sourceCurrency">
                      Source Currency
                      <span class="text-danger">*</span>
                    </label>
                    <select
                      class="form-control"
                      id="sourceCurrency"
                      v-model="quickRate.source_country_id"
                      :class="{ 'is-invalid': errors.source_currency }"
                      :disabled="isLoadingCurrencies"
                    >
                      <option value="">
                        {{
                          isLoadingCurrencies
                            ? "Loading currencies..."
                            : "Select Source Currency"
                        }}
                      </option>
                      <option
                        v-for="currency in sourceCurrencies"
                        :key="currency.countryId"
                        :value="currency.countryId"
                      >
                        {{ currency.currencyCode }} - {{ currency.countryName }}
                      </option>
                    </select>
                    <div v-if="errors.source_currency" class="invalid-feedback">
                      {{ errors.source_currency }}
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="targetCurrency">
                      Target Currency
                      <span class="text-danger">*</span>
                    </label>
                    <select
                      class="form-control"
                      id="targetCurrency"
                      v-model="quickRate.target_country_id"
                      :class="{ 'is-invalid': errors.target_currency }"
                      :disabled="isLoadingCurrencies"
                    >
                      <option value="">
                        {{
                          isLoadingCurrencies
                            ? "Loading currencies..."
                            : "Select Target Currency"
                        }}
                      </option>
                      <option
                        v-for="currency in destinationCurrencies"
                        :key="currency.countryId"
                        :value="currency.countryId"
                      >
                        {{ currency.currencyCode }} - {{ currency.countryName }}
                      </option>
                    </select>
                    <div v-if="errors.target_currency" class="invalid-feedback">
                      {{ errors.target_currency }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="rate">
                      Exchange Rate
                      <span class="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      id="rate"
                      v-model="quickRate.rate"
                      placeholder="Enter exchange rate (e.g., 12.50)"
                      :class="{ 'is-invalid': errors.rate }"
                    />
                    <small class="form-text text-muted">
                      Format: Positive number with up to 8 decimal places (e.g.,
                      12.50, 0.00123456)
                    </small>
                    <div v-if="errors.rate" class="invalid-feedback">
                      {{ errors.rate }}
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="comments">Comments (Optional)</label>
                    <textarea
                      class="form-control"
                      id="comments"
                      v-model="quickRate.comments"
                      rows="3"
                      placeholder="Add any comments about this rate update (max 2000 characters)"
                      maxlength="2000"
                    ></textarea>
                    <small class="form-text text-muted">
                      {{ (quickRate.comments || "").length }}/2000 characters
                    </small>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <button
                    class="btn btn-primary"
                    @click="updateQuickRate"
                    :disabled="isSubmitting"
                  >
                    <i class="fa fa-save mr-2"></i>
                    {{ isSubmitting ? "Updating..." : "Update Rate" }}
                  </button>
                  <button
                    class="btn btn-secondary ml-2"
                    @click="resetQuickRate"
                    :disabled="isSubmitting"
                  >
                    <i class="fa fa-refresh mr-2"></i>
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "@/plugins/sweetalert2";
import "@/plugins/vee-validate";
import { ArchBaseService } from "@/common/api.service";

export default {
  name: "QuickRateUpdateComponent",
  components: {
    BreadCrumb: () => import("@/components/BreadCrumb.vue"),
  },
  data() {
    return {
      quickRate: {
        source_country_id: "",
        target_country_id: "",
        rate: "",
        comments: "",
      },
      errors: {},
      isSubmitting: false,
      sourceCurrencies: [],
      destinationCurrencies: [],
      isLoadingCurrencies: true,
    };
  },
  async mounted() {
    await this.fetchCurrencies();
  },
  methods: {
    async fetchCurrencies() {
      try {
        this.isLoadingCurrencies = true;

        // Set authentication headers
        ArchBaseService.setHeader();

        // Fetch source currencies
        const sourceResponse = await ArchBaseService.get(
          "fx-rates/source-currencies",
        );
        this.sourceCurrencies = sourceResponse.data;

        // Fetch destination currencies
        const destinationResponse = await ArchBaseService.get(
          "fx-rates/destination-countries",
        );
        this.destinationCurrencies = destinationResponse.data;

        console.log("Source currencies loaded:", this.sourceCurrencies.length);
        console.log(
          "Destination currencies loaded:",
          this.destinationCurrencies.length,
        );
      } catch (error) {
        console.error("Error fetching currencies:", error);
        this.$swal({
          title: "Error!",
          text: "Failed to load currencies. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        this.isLoadingCurrencies = false;
      }
    },
    validateForm() {
      this.errors = {};

      if (!this.quickRate.source_country_id) {
        this.errors.source_currency = "Source currency is required";
      }

      if (!this.quickRate.target_country_id) {
        this.errors.target_currency = "Target currency is required";
      }

      if (
        this.quickRate.source_country_id &&
        this.quickRate.target_country_id
      ) {
        const selectedSource = this.sourceCurrencies.find(
          (c) => c.countryId === this.quickRate.source_country_id,
        );
        const selectedTarget = this.destinationCurrencies.find(
          (c) => c.countryId === this.quickRate.target_country_id,
        );
        if (
          selectedSource &&
          selectedTarget &&
          selectedSource.currencyCode === selectedTarget.currencyCode
        ) {
          this.errors.target_currency =
            "Source and target currencies cannot be the same";
        }
      }

      // Validate rate format: positive number with up to 8 decimal places
      const rateRegex = /^(?:0|[1-9]\d*)(?:\.\d{1,8})?$/;
      if (!this.quickRate.rate || !rateRegex.test(this.quickRate.rate)) {
        this.errors.rate =
          "Rate must be a positive number with up to 8 decimal places";
      }

      // Validate comments length
      if (this.quickRate.comments && this.quickRate.comments.length > 2000) {
        this.errors.comments = "Comments cannot exceed 2000 characters";
      }

      return Object.keys(this.errors).length === 0;
    },

    async updateQuickRate() {
      if (!this.validateForm()) {
        console.log("Form validation failed:", this.errors);
        return;
      }

      console.log("Starting rate update process...");
      console.log("Form data:", this.quickRate);

      this.isSubmitting = true;

      try {
        // Set authentication headers
        ArchBaseService.setHeader();

        // Look up currency codes from selected country IDs
        const selectedSource = this.sourceCurrencies.find(
          (c) => c.countryId === this.quickRate.source_country_id,
        );
        const selectedTarget = this.destinationCurrencies.find(
          (c) => c.countryId === this.quickRate.target_country_id,
        );

        // Prepare the request payload according to ManualUpdateDto
        const payload = {
          source_currency: selectedSource.currencyCode,
          target_currency: selectedTarget.currencyCode,
          source_country_id: this.quickRate.source_country_id,
          target_country_id: this.quickRate.target_country_id,
          rate: this.quickRate.rate,
        };

        // Only include comments if it has a value
        if (this.quickRate.comments && this.quickRate.comments.trim()) {
          payload.comments = this.quickRate.comments.trim();
        }

        console.log("Prepared payload for API:", payload);
        console.log("Making API request to /fx-rates/manual-update...");

        // Call the API endpoint
        const response = await ArchBaseService.post(
          "fx-rates/manual-update",
          payload,
        );

        console.log("API Response received:", response);
        console.log("Response status:", response.status);
        console.log("Response data:", response.data);

        if (response.status === 201) {
          console.log("Rate update successful!");
          this.$swal({
            title: "Success!",
            text: "Exchange rate updated successfully",
            icon: "success",
            confirmButtonText: "OK",
          });

          this.resetQuickRate();
        } else {
          console.warn("Unexpected response status:", response.status);
          throw new Error("Failed to update exchange rate");
        }
      } catch (error) {
        console.error("Error updating exchange rate:", error);
        console.error("Error details:", {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers,
        });

        let errorMessage = "Failed to update exchange rate. Please try again.";

        if (error.response && error.response.data) {
          console.log("API returned error response:", error.response.data);

          // Handle validation errors from the API
          if (error.response.data.message) {
            errorMessage = error.response.data.message;
            console.log("Using API error message:", errorMessage);
          } else if (error.response.data.errors) {
            // Handle field-specific validation errors
            const apiErrors = error.response.data.errors;
            console.log("API validation errors:", apiErrors);

            Object.keys(apiErrors).forEach((field) => {
              if (Object.prototype.hasOwnProperty.call(this.errors, field)) {
                this.errors[field] = apiErrors[field][0]; // Take first error message
                console.log(
                  `Setting field error for ${field}:`,
                  apiErrors[field][0],
                );
              }
            });
            errorMessage = "Please correct the errors above.";
          }
        } else {
          console.log("No response data in error, using generic message");
        }

        console.log("Displaying error to user:", errorMessage);
        this.$swal({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        console.log(
          "Rate update process completed, setting isSubmitting to false",
        );
        this.isSubmitting = false;
      }
    },

    resetQuickRate() {
      this.quickRate = {
        source_country_id: "",
        target_country_id: "",
        rate: "",
        comments: "",
      };
      this.errors = {};
    },
  },
};
</script>

<style scoped>
.form-control.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #dc3545;
}

.form-text {
  font-size: 80%;
  color: #6c757d;
}
</style>
